import React, { useEffect, useState, useRef } from 'react';
import Header from '../../components/ui/Header';
import { useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { Auth } from 'aws-amplify';
import * as Const from '../../utils/Const';
import { GetErrorMessages, fetchTokenAndOrgId } from '../../utils/UtilFunction';

interface CustomAuthenticatorProps {
  children?: React.ReactNode;
  hideSignUp?: boolean;
}

interface ResetPasswordProps {
  username: string;
  code: string;
  newPassword: string;
}

Auth.configure({
  authenticationFlowType: 'USER_SRP_AUTH',
});

function CustomAuthenticator({ children }: CustomAuthenticatorProps) {
  const [step, setStep] = useState('login');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [user, setUser] = useState(null);
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [resetPassword, setResetPassword] = useState<ResetPasswordProps>({
    username: '',
    code: '',
    newPassword: '',
  });
  const navigate = useNavigate();
  const firstPasswordInputRef = useRef<HTMLInputElement>(null);
  const resetPasswordInputRef = useRef<HTMLInputElement>(null);
  const [username, setUsername] = useState('');

  useEffect(() => {
    const checkCurrentUser = async () => {
      try {
        await Auth.currentAuthenticatedUser();
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
      } finally {
        setLoadingAuth(false);
      }
    };
    checkCurrentUser();
  }, [isAuthenticated]);

  if (loadingAuth) {
    return (
      <div className="flex justify-center items-center min-h-screen w-full">
        <CircularProgress />
      </div>
    );
  }

  const checkCurrentUser = async () => {
    try {
      await Auth.currentAuthenticatedUser();
      setIsAuthenticated(true);
    } catch (error) {
      setIsAuthenticated(false);
    }
  };

  const submitSignIn = async () => {
    try {
      const signedInUser = await Auth.signIn(username, password);
      if (signedInUser.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setUser(signedInUser);
        setStep('changePassword');
        setError('');
      } else {
        setUser(signedInUser);
        setStep('verify');
        setError('');
      }
    } catch (err: any) {
      console.error('Error signing in: ', err);
      const msg =
        GetErrorMessages(err.message) || Const.ErrorMessageExceptionJapanese + '\n' + err.message;
      setError(msg);
    }
  };

  const getOrgId = async () => {
    let result = false;
    try {
      const { isGetOrgId } = await fetchTokenAndOrgId(username);

      if (isGetOrgId) {
        result = true;
      } else {
        setUser(null);
        setStep('login');
        setError(Const.ErrorMessageSignIn);
      }
    } catch (err: any) {
      console.error('Error signing in: ', err);
      const msg =
        GetErrorMessages(err.message) || Const.ErrorMessageExceptionJapanese + '\n' + err.message;
      setError(msg);
    }
    return result;
  };

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      setIsAuthenticated(false);
      localStorage.removeItem(Const.KeySessionBaseData);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const submitSignInWithLoading = async () => {
    try {
      setLoading(true);
      await submitSignIn();
      const isSuccess = await getOrgId();
      // setError('');
      if (isSuccess) {
        setIsAuthenticated(true);
        navigate('/dashboard');
      }
    } catch (error: any) {
      console.error('Error signing in: ', error);
      const msg =
        GetErrorMessages(error.message) ||
        Const.ErrorMessageExceptionJapanese + '\n' + error.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Password reset
   */

  const requestPasswordReset = async () => {
    try {
      await Auth.forgotPassword(resetPassword.username);
      setStep('setNewPassword');
      setError('');
    } catch (err: any) {
      const msg =
        GetErrorMessages(err.message) || Const.ErrorMessageExceptionJapanese + '\n' + err.message;
      setError(msg);
    }
  };

  const submitNewPassword = async () => {
    try {
      await Auth.forgotPasswordSubmit(
        resetPassword.username,
        resetPassword.code,
        resetPassword.newPassword
      );
      setStep('login');
      setError('');
      setResetPassword({ username: '', code: '', newPassword: '' });
    } catch (err: any) {
      const msg =
        GetErrorMessages(err.message) || Const.ErrorMessageExceptionJapanese + '\n' + err.message;
      setError(msg);
    }
  };

  const submitNewPasswordFirst = async (newPassword: string) => {
    try {
      await Auth.completeNewPassword(user, newPassword);

      await Auth.signOut();
      setStep('login');
      setIsAuthenticated(false);
      setResetPassword({ ...resetPassword, newPassword: '' });
      setUsername('');
    } catch (err: any) {
      const msg =
        GetErrorMessages(err.message) || Const.ErrorMessageExceptionJapanese + '\n' + err.message;
      setError(msg);
    }
  };

  const checkPasswordRequirements = (e: React.FormEvent<HTMLFormElement>) => {
    let targetInput: HTMLInputElement | null = null;
    let checkOK = false;
    const formName = e.currentTarget.name;

    if (formName === 'firstPassword') {
      targetInput = firstPasswordInputRef.current;
    } else {
      targetInput = resetPasswordInputRef.current;
    }

    const tempPassword = targetInput ? targetInput.value : '';
    if (!tempPassword) {
      targetInput?.setCustomValidity(Const.ErrorMessageRequiredInput);
      targetInput?.reportValidity();
      return false;
    }

    if (
      tempPassword.length >= 8 &&
      /[A-Z]/.test(tempPassword) &&
      /[a-z]/.test(tempPassword) &&
      /[0-9]/.test(tempPassword) &&
      /^[\S]+.*[\S]+$/.test(tempPassword)
    ) {
      checkOK = true;
    }

    if (checkOK) {
      targetInput?.setCustomValidity('');
      return true;
    } else {
      targetInput?.setCustomValidity(Const.ErrorMessagePasswordRequirements);
      targetInput?.reportValidity();
      return false;
    }
  };

  return (
    <div>
      <div>
        {!isAuthenticated && (
          <div className="flex items-center justify-center fixed top-0 left-0 w-full h-screen bg-gradient-to-b from-transparent to-black">
            {!isAuthenticated && step === 'changePassword' && (
              <form
                name="firstPassword"
                className="bg-gray-900 w-96 p-8 rounded-md shadow-xl"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!checkPasswordRequirements(e)) {
                    return;
                  } else {
                    submitNewPasswordFirst(newPassword);
                  }
                }}
              >
                <h1 className="text-3xl font-bold text-white mb-4 text-center">Set New Password</h1>
                <input
                  ref={firstPasswordInputRef}
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New Password"
                  className="w-full p-2 mt-4 bg-transparent border border-white rounded-md text-white placeholder-white"
                  onInput={(e) => e.currentTarget.setCustomValidity('')}
                />
                <button
                  type="submit"
                  className="w-full mt-4 p-2 bg-white text-black rounded-md hover:opacity-80 active:opacity-60"
                >
                  Set New Password
                </button>
                {error &&
                  error.split('\n').map((line, idx) => (
                    <p className="text-red-500">
                      <React.Fragment key={idx}>
                        {line}
                        <br />
                      </React.Fragment>
                    </p>
                  ))}
              </form>
            )}

            {!isAuthenticated && step === 'resetPassword' && (
              <form
                className="bg-gray-900 w-96 p-8 rounded-md shadow-xl"
                onSubmit={(e) => {
                  e.preventDefault();
                  requestPasswordReset();
                }}
              >
                <h1 className="text-3xl font-bold text-white mb-4 text-center">Reset Password</h1>
                <input
                  value={resetPassword.username}
                  onChange={(e) => {
                    setResetPassword({
                      ...resetPassword,
                      username: e.target.value,
                    });
                  }}
                  placeholder="Username"
                  className="w-full p-2 mt-4 bg-transparent border border-white rounded-md text-white placeholder-white"
                  required
                  onInvalid={(e) =>
                    e.currentTarget.setCustomValidity(Const.ErrorMessageRequiredInput)
                  }
                  onInput={(e) => e.currentTarget.setCustomValidity('')}
                />
                <button
                  type="submit"
                  className="w-full mt-4 p-2 bg-white text-black rounded-md hover:opacity-80 active:opacity-60"
                >
                  {loading ? (
                    <div className="flex justify-center items-center">
                      <CircularProgress size={24} />
                    </div>
                  ) : (
                    'Send Reset Code'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep('login');
                    setError('');
                    setResetPassword({
                      ...resetPassword,
                      username: '',
                    });
                  }}
                  className="w-1/3 mt-4 p-2 bg-slate-400 text-white rounded-md hover:opacity-80 active:opacity-60"
                >
                  Cancel
                </button>
                {error &&
                  error.split('\n').map((line, idx) => (
                    <p className="text-red-500">
                      <React.Fragment key={idx}>
                        {line}
                        <br />
                      </React.Fragment>
                    </p>
                  ))}
              </form>
            )}

            {!isAuthenticated && step === 'setNewPassword' && (
              <form
                name="resetPassword"
                className="bg-gray-900 w-96 p-8 rounded-md shadow-xl"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!checkPasswordRequirements(e)) {
                    return;
                  } else {
                    submitNewPassword();
                  }
                }}
              >
                <h1 className="text-3xl font-bold text-white mb-4 text-center">Set New Password</h1>
                <input
                  value={resetPassword.code}
                  onChange={(e) => {
                    setResetPassword({ ...resetPassword, code: e.target.value });
                  }}
                  placeholder="Verification Code"
                  className="w-full p-2 mt-4 bg-transparent border border-white rounded-md text-white placeholder-white"
                  required
                  onInvalid={(e) =>
                    e.currentTarget.setCustomValidity(Const.ErrorMessageRequiredInput)
                  }
                  onInput={(e) => e.currentTarget.setCustomValidity('')}
                />
                <input
                  ref={resetPasswordInputRef}
                  type="password"
                  value={resetPassword.newPassword}
                  onChange={(e) => {
                    setResetPassword({
                      ...resetPassword,
                      newPassword: e.target.value,
                    });
                  }}
                  placeholder="New Password"
                  className="w-full p-2 mt-4 bg-transparent border border-white rounded-md text-white placeholder-white"
                  onInput={(e) => e.currentTarget.setCustomValidity('')}
                />
                <button
                  type="submit"
                  className="w-full mt-4 p-2 bg-white text-black rounded-md hover:opacity-80 active:opacity-60"
                >
                  {loading ? (
                    <div className="flex justify-center items-center">
                      <CircularProgress size={24} />
                    </div>
                  ) : (
                    'Set New Password'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setStep('login');
                    setError('');
                    setResetPassword({ username: '', code: '', newPassword: '' });
                  }}
                  className="w-1/3 mt-4 p-2 bg-slate-400 text-white rounded-md hover:opacity-80 active:opacity-60"
                >
                  Cancel
                </button>
                {error &&
                  error.split('\n').map((line, idx) => (
                    <p className="text-red-500">
                      <React.Fragment key={idx}>
                        {line}
                        <br />
                      </React.Fragment>
                    </p>
                  ))}
              </form>
            )}

            {!isAuthenticated && step === 'login' && (
              <form
                className="bg-gray-900 w-96 p-8 rounded-md shadow-xl"
                onSubmit={(e) => {
                  e.preventDefault();
                  submitSignInWithLoading();
                }}
              >
                <h1 className="text-3xl font-bold text-white items-center text-center mb-2">
                  Sign in
                </h1>
                <input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Username"
                  className="w-full p-2 mt-4 bg-transparent border border-white rounded-md text-white placeholder-white"
                  required
                  onInvalid={(e) =>
                    e.currentTarget.setCustomValidity(Const.ErrorMessageRequiredInput)
                  }
                  onInput={(e) => e.currentTarget.setCustomValidity('')}
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full p-2 mt-4 bg-transparent border border-white rounded-md text-white placeholder-white"
                  required
                  onInvalid={(e) =>
                    e.currentTarget.setCustomValidity(Const.ErrorMessageRequiredInput)
                  }
                  onInput={(e) => e.currentTarget.setCustomValidity('')}
                />
                <button
                  type="submit"
                  className="w-full mt-4 p-2 bg-white text-black rounded-md hover:opacity-80 active:opacity-60"
                >
                  {loading ? (
                    <div className="flex justify-center items-center">
                      <CircularProgress size={24} />
                    </div>
                  ) : (
                    'Login'
                  )}
                </button>
                <div className="text-center text-white mt-2 hover:text-red-300">
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setStep('resetPassword');
                      setError('');
                      setPassword('');
                      setUsername('');
                    }}
                  >
                    Forgot password?
                  </a>
                </div>
                {error &&
                  error.split('\n').map((line, idx) => (
                    <p className="text-red-500">
                      <React.Fragment key={idx}>
                        {line}
                        <br />
                      </React.Fragment>
                    </p>
                  ))}
              </form>
            )}
          </div>
        )}

        {isAuthenticated && (
          <>
            <Header onLogout={handleLogout} checkCurrentUser={checkCurrentUser} />
            {children}
          </>
        )}
      </div>
    </div>
  );
}

export default CustomAuthenticator;
